/**
 * StatusBadge — the one inline status pill for the story page (and every
 * other status surface, per ADR-0083). It renders, in order:
 *   - the per-tone glyph (aria-hidden — the non-colour cue for sighted users)
 *   - the real status WORD (the meaning, for everyone incl. assistive tech)
 *
 * Tone resolves through the shared `statusTone()` (unknown → inert); colour
 * comes ONLY from the AA-verified `--color-status-<tone>-*` token pairs.
 * Raw Tailwind tint pairs are banned (a11y contract #5). The status word is
 * supplied by the caller already humanized (e.g. "Partially Paid"); the raw
 * status string drives the tone.
 */

import { forwardRef, type HTMLAttributes } from "react";

import { cn } from "../lib/utils";
import { StatusGlyph, statusTone, toneSpec } from "./status-glyph";

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Raw status string — drives the tone via the shared statusTone(). */
  status: string;
  /** The human-readable status word to display. Defaults to `status`. */
  label?: string;
  size?: number;
}

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, label, size = 12, className, ...props }, ref) => {
    const tone = statusTone(status);
    const spec = toneSpec(tone);
    const word = label ?? status;
    return (
      <span
        ref={ref}
        data-status-tone={tone}
        className={cn(
          "inline-flex items-center gap-1 rounded-[var(--radius-full)]",
          "border border-transparent px-2 py-0.5 text-[12px] font-medium",
          className,
        )}
        style={{
          backgroundColor: `var(${spec.bg})`,
          color: `var(${spec.text})`,
        }}
        {...props}
      >
        <StatusGlyph tone={tone} size={size} />
        <span>{word}</span>
      </span>
    );
  },
);
StatusBadge.displayName = "StatusBadge";
